import { useEffect, useState } from 'react'
import { getUserFiles } from '@/uppy-file-upload/fetch/get-user-files'

/**
 * Given the `user_file_id`s listed on a Medical Chronology / Timeline source-file list, returns the
 * subset the active-files batch endpoint no longer resolves for this member.
 *
 * That endpoint is scoped to active files reachable through matter membership
 * (`get_member_active_files_by_ids`), so an id comes back absent when the file was soft-deleted
 * **or** when it is simply no longer reachable by this member — for example after being moved to a
 * matter they do not belong to. Callers present both as deleted because no field separates them;
 * the second case is the known imprecision in this signal.
 *
 * Fails open: a fetch failure flags nothing rather than flagging every row, since a screen full of
 * false "deleted" badges is far worse than a missing one. `getUserFiles` already reports the failure
 * to Sentry at the endpoint boundary, so swallowing it here loses no diagnostics.
 */
export function useDeletedFileIds(userFileIds: string[]): { deletedFileIds: Set<string> } {
  const [deletedFileIds, setDeletedFileIds] = useState<Set<string>>(new Set())
  // UUIDs cannot contain a comma, so this round-trips losslessly and gives the effect a stable
  // dependency without depending on the caller passing a memoized array.
  const key = userFileIds.join(',')

  useEffect(() => {
    const ids = key ? key.split(',') : []
    if (ids.length === 0) {
      setDeletedFileIds(new Set())
      return
    }

    let cancelled = false

    getUserFiles(ids)
      .then((response) => {
        if (cancelled) return
        const activeIds = new Set(Object.keys(response.files))
        setDeletedFileIds(new Set(ids.filter((id) => !activeIds.has(id))))
      })
      .catch(() => {
        if (!cancelled) setDeletedFileIds(new Set())
      })

    return () => {
      cancelled = true
    }
  }, [key])

  return { deletedFileIds }
}
